<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { COLLECTION_STATUS } from '@hsl-ems/contracts';

import { p1Api } from '@/lib/p1-api';
import { p4Api, type RepaymentCollectionRecord } from '@/lib/p4-api';

const router = useRouter();
const records = ref<RepaymentCollectionRecord[]>([]);
const loading = ref(true);
const submitting = ref(false);
const error = ref('');
const message = ref('');
const selectedId = ref('');
const view = ref<'PENDING' | 'DONE'>('PENDING');
const confirmedAmount = ref('');
const collectedAt = ref('');
const collectionMethod = ref('');
const referenceNo = ref('');
const proofFileId = ref('');
const rejectionReason = ref('');

const selected = computed(() => records.value.find((record) => record.id === selectedId.value));
const filtered = computed(() =>
  records.value.filter((record) =>
    view.value === 'PENDING'
      ? record.cashRepayment.collectionStatus === COLLECTION_STATUS.PENDING_CONFIRMATION
      : record.cashRepayment.collectionStatus !== COLLECTION_STATUS.PENDING_CONFIRMATION,
  ),
);
const cleanProofs = computed(
  () => selected.value?.files.filter((link) => link.file.status === 'CLEAN') ?? [],
);

onMounted(load);

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const me = await p1Api.me();
    if (me.mustChangePassword) return void (await router.replace('/change-password'));
    if (!me.permissions.includes('collection:confirm')) {
      error.value = '当前账号没有现金还款到账确认权限';
      return;
    }
    records.value = await p4Api.repaymentCollections();
    if (selectedId.value && !records.value.some((record) => record.id === selectedId.value))
      selectedId.value = '';
  } catch (reason) {
    const detail = reason instanceof Error ? reason.message : '还款确认任务加载失败';
    if (detail.includes('登录已失效')) return void (await router.replace('/login'));
    error.value = detail;
  } finally {
    loading.value = false;
  }
}

function select(record: RepaymentCollectionRecord) {
  selectedId.value = record.id;
  confirmedAmount.value = record.cashRepayment.amount;
  collectedAt.value = localDateTime(new Date());
  collectionMethod.value = '';
  referenceNo.value = '';
  proofFileId.value = record.files.find((link) => link.file.status === 'CLEAN')?.file.id ?? '';
  rejectionReason.value = '';
  error.value = '';
  message.value = '';
}

async function confirm() {
  const record = selected.value;
  if (!record) return;
  if (
    !/^\d{1,16}(?:\.\d{1,2})?$/.test(confirmedAmount.value) ||
    !/^[A-Z][A-Z0-9_-]{1,63}$/.test(collectionMethod.value) ||
    referenceNo.value.trim().length < 2 ||
    !collectedAt.value ||
    !proofFileId.value
  ) {
    error.value = '请填写确认金额、收款时间、方式代码和参考流水，并选择已通过扫描的还款凭证';
    return;
  }
  await mutate(async () => {
    await p4Api.confirmRepaymentCollection(record.id, {
      documentVersion: record.version,
      collectionVersion: record.cashRepayment.version,
      confirmedAmount: confirmedAmount.value,
      collectedAt: new Date(collectedAt.value).toISOString(),
      collectionMethod: collectionMethod.value,
      referenceNo: referenceNo.value.trim(),
      proofFileId: proofFileId.value,
    });
    message.value = '到账确认成功，借款余额已同步核减';
  });
}

async function reject() {
  const record = selected.value;
  if (!record) return;
  if (rejectionReason.value.trim().length < 2) {
    error.value = '拒绝确认必须填写至少 2 个字的原因';
    return;
  }
  await mutate(async () => {
    await p4Api.rejectRepaymentCollection(record.id, {
      documentVersion: record.version,
      collectionVersion: record.cashRepayment.version,
      reason: rejectionReason.value.trim(),
    });
    message.value = '已拒绝本次到账确认，借款余额未发生变化';
  });
}

async function mutate(action: () => Promise<void>) {
  submitting.value = true;
  error.value = '';
  message.value = '';
  try {
    await action();
    selectedId.value = '';
    await load();
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '还款确认操作失败';
  } finally {
    submitting.value = false;
  }
}

function money(value: string) {
  return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(
    Number(value),
  );
}

function dateTime(value: string | null) {
  if (!value) return '未设置';
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function localDateTime(value: Date) {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
</script>

<template>
  <main class="workspace-shell payment-shell">
    <header class="workspace-header">
      <RouterLink class="workspace-brand" to="/documents">
        <span class="brand-mark" aria-hidden="true">郎</span>
        <span><strong>郎报通</strong><small>还款确认工作台</small></span>
      </RouterLink>
      <nav aria-label="主导航">
        <RouterLink to="/documents">我的单据</RouterLink>
        <RouterLink to="/approval-tasks">审批待办</RouterLink>
        <RouterLink to="/payment-tasks">付款处理</RouterLink>
        <RouterLink to="/payment-corrections">付款更正</RouterLink>
        <RouterLink class="active" to="/repayment-collections">还款确认</RouterLink>
        <RouterLink to="/loan-offsets">借款抵扣</RouterLink>
        <RouterLink to="/admin">组织与权限</RouterLink>
      </nav>
      <RouterLink class="text-link" to="/login">切换账号</RouterLink>
    </header>

    <section class="page-heading">
      <div>
        <p class="eyebrow">P4 · 现金还款到账</p>
        <h1>还款确认工作台</h1>
        <p>审批通过不核减余额；只有财务核实线下资金到账并确认后，系统才追加借款余额流水。</p>
      </div>
      <button class="quiet-button" type="button" :disabled="loading" @click="load">刷新</button>
    </section>

    <div class="approval-tabs" role="tablist" aria-label="到账确认视图">
      <button
        type="button"
        role="tab"
        :class="{ active: view === 'PENDING' }"
        @click="view = 'PENDING'"
      >
        待确认
      </button>
      <button type="button" role="tab" :class="{ active: view === 'DONE' }" @click="view = 'DONE'">
        已处理
      </button>
    </div>

    <p v-if="error" class="inline-alert inline-alert--error" role="alert">{{ error }}</p>
    <p v-if="message" class="inline-alert" role="status">{{ message }}</p>
    <section v-if="loading" class="document-empty">正在加载还款确认任务…</section>
    <section v-else-if="!filtered.length" class="document-empty">
      <strong>{{ view === 'PENDING' ? '当前没有待确认到账任务' : '当前没有已处理记录' }}</strong>
      <p>现金还款完成审批后会进入待确认列表。</p>
    </section>
    <section v-else class="payment-layout">
      <div class="approval-list payment-list">
        <button
          v-for="record in filtered"
          :key="record.id"
          type="button"
          class="approval-row payment-row"
          :class="{ selected: selectedId === record.id }"
          @click="select(record)"
        >
          <span class="document-icon">还</span>
          <span class="document-main">
            <span class="document-title">
              <strong>{{ record.documentNo }}</strong>
              <span class="status-pill" :data-status="record.cashRepayment.collectionStatus">
                {{ record.cashRepayment.collectionStatus }}
              </span>
            </span>
            <span
              >{{ record.applicant.name }} · 来源
              {{ record.cashRepayment.loanDocument.documentNo }}</span
            >
            <small
              >当前借款余额 {{ money(record.availableLoanBalance) }} ·
              {{ record.description }}</small
            >
          </span>
          <strong class="document-amount">{{ money(record.cashRepayment.amount) }}</strong>
        </button>
      </div>

      <aside v-if="selected" class="form-section payment-operation-panel">
        <div class="section-heading-row">
          <div>
            <p class="eyebrow">{{ selected.documentNo }}</p>
            <h2>核实线下到账</h2>
          </div>
          <button class="text-button" type="button" @click="selectedId = ''">关闭</button>
        </div>
        <dl class="payment-facts">
          <div>
            <dt>申请人</dt>
            <dd>{{ selected.applicant.name }}</dd>
          </div>
          <div>
            <dt>申请金额</dt>
            <dd>{{ money(selected.cashRepayment.amount) }}</dd>
          </div>
          <div>
            <dt>来源借款</dt>
            <dd>{{ selected.cashRepayment.loanDocument.documentNo }}</dd>
          </div>
          <div>
            <dt>当前余额</dt>
            <dd>{{ money(selected.availableLoanBalance) }}</dd>
          </div>
        </dl>

        <form
          v-if="selected.cashRepayment.collectionStatus === COLLECTION_STATUS.PENDING_CONFIRMATION"
          class="payment-form"
          @submit.prevent="confirm"
        >
          <label
            ><span>确认到账金额</span
            ><input v-model.trim="confirmedAmount" inputmode="decimal" required
          /></label>
          <label
            ><span>实际收款时间</span><input v-model="collectedAt" type="datetime-local" required
          /></label>
          <label>
            <span>收款方式配置代码</span>
            <input
              v-model.trim="collectionMethod"
              maxlength="64"
              placeholder="例如 BANK_TRANSFER"
              required
            />
          </label>
          <label
            ><span>参考流水号</span><input v-model.trim="referenceNo" maxlength="128" required
          /></label>
          <label>
            <span>还款凭证</span>
            <select v-model="proofFileId" required>
              <option value="">请选择已通过安全扫描的凭证</option>
              <option v-for="link in cleanProofs" :key="link.file.id" :value="link.file.id">
                {{ link.file.originalName }}
              </option>
            </select>
          </label>
          <button class="primary-button" type="submit" :disabled="submitting">
            确认资金已到账
          </button>
          <div class="payment-failure-block">
            <label
              ><span>拒绝原因</span
              ><textarea v-model.trim="rejectionReason" maxlength="1000" rows="3" />
            </label>
            <button class="danger-button" type="button" :disabled="submitting" @click="reject">
              拒绝到账确认
            </button>
          </div>
        </form>
        <div v-else class="payment-history">
          <article>
            <strong>{{ selected.cashRepayment.collectionStatus }}</strong>
            <small>处理时间：{{ dateTime(selected.cashRepayment.decisionAt) }}</small>
            <p v-if="selected.cashRepayment.confirmedAmount">
              确认金额：{{ money(selected.cashRepayment.confirmedAmount) }} · 流水号：{{
                selected.cashRepayment.referenceNo
              }}
            </p>
            <p v-if="selected.cashRepayment.rejectionReason">
              {{ selected.cashRepayment.rejectionReason }}
            </p>
          </article>
        </div>
      </aside>
    </section>

    <nav class="mobile-nav" aria-label="移动端主导航">
      <RouterLink to="/documents">单据</RouterLink>
      <RouterLink to="/payment-tasks">付款</RouterLink>
      <RouterLink class="active" to="/repayment-collections">还款</RouterLink>
    </nav>
  </main>
</template>
